import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CURRENT_BRAND } from '../services/brandService/queries';

const promises = new Map<string, Promise<Stripe | null>>();

/** One `loadStripe` per key. `Elements` must get the same promise on every render. */
export const stripePromiseForKey = (publishableKey: string): Promise<Stripe | null> => {
  let promise = promises.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey);
    promises.set(publishableKey, promise);
  }
  return promise;
};

/**
 * The viewing brand's own key when it has one, else the Autopilotee key from the build, which a
 * brand without its own Stripe account keeps using.
 */
export const pickPublishableKey = (
  brandKey?: string | null,
  buildKey: string = process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? '',
) => brandKey?.trim() || buildKey;

/**
 * The Stripe.js instance for the viewing brand.
 *
 * A brand with its own Stripe account (D12) creates its PaymentIntents there, so the page has
 * to confirm them with that account's publishable key. With the Autopilotee key from the build,
 * Stripe answers "No such payment_intent".
 *
 * `ready` is false until the brand query has answered. `Elements` cannot change its `stripe`
 * prop after it starts, so render it only when `ready`. If the query fails, the build key is used.
 */
export const useStripePromise = (): {
  stripePromise: Promise<Stripe | null> | null;
  publishableKey: string;
  ready: boolean;
} => {
  const { data, loading } = useQuery(CURRENT_BRAND);
  const publishableKey = pickPublishableKey(data?.currentBrand?.stripePublishableKey);
  const stripePromise = useMemo(
    () => (loading ? null : stripePromiseForKey(publishableKey)),
    [loading, publishableKey],
  );
  return { stripePromise, publishableKey, ready: !loading && stripePromise !== null };
};
